import {
  CurecallSearchableSelect,
  CurecallButton,
  ECurecallButtonAppearance,
  CurecallSearchableSelectItem,
  CurecallTag,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { TMedAlert, MED_ALERT_UI_TO_TAG_STATUS } from 'src/types/med-alert'
import { useMemo, useState, useRef, useEffect } from 'react'
import { useProjectMedAlerts } from '../use-project-med-alerts.hook'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { useLocale } from 'src/hooks/use-locale'

interface ExistingMedAlertTabProps {
  onCancel: () => void
  onValidate: (medAlertId: number) => void
  onSwitchToNewMedAlert: () => void
}

export function ExistingMedAlertTab({
  onCancel,
  onValidate,
  onSwitchToNewMedAlert,
}: Readonly<ExistingMedAlertTabProps>) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { data: medAlerts = [] } = useProjectMedAlerts()

  const [selectedMedAlert, setSelectedMedAlert] = useState<TMedAlert | null>(null)
  const [showError, setShowError] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus the first input when component mounts
    const firstInput = containerRef.current?.querySelector('input')
    firstInput?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleValidateExisting()
    }
  }

  const MEDALERT_OPTIONS = useMemo(
    () =>
      medAlerts
        .map((medAlert: TMedAlert) => {
          const alertLabel = getTranslatedLabel(locale, medAlert.intlLabel)
          return {
            label: alertLabel,
            value: medAlert.id,
            displayLabel: (
              <CurecallTag status={MED_ALERT_UI_TO_TAG_STATUS[medAlert.ui]}>{alertLabel}</CurecallTag>
            ),
          }
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [medAlerts, locale],
  )

  const handleMedAlertSelect = (medAlert: CurecallSearchableSelectItem) => {
    showError && setShowError(false)
    const foundMedAlert = medAlerts.find((m: TMedAlert) => m.id === medAlert.value)
    setSelectedMedAlert(foundMedAlert || null)
  }

  const handleValidateExisting = () => {
    if (selectedMedAlert) {
      onValidate(selectedMedAlert.id)
    } else {
      setShowError(true)
    }
  }

  if (MEDALERT_OPTIONS.length === 0) {
    return (
      <div className="c-flex c-flex-col c-items-center c-justify-center c-py-12 c-px-4">
        <div className="c-text-center c-max-w-md">
          <h3 className="c-text-lg c-font-semibold c-text-gray-900 c-mb-2">
            {t('pages.configuration.followups.chatbot.med-alert-modal.no-med-alerts-available')}
          </h3>
          <p className="c-text-sm c-text-gray-600 c-mb-6">
            {t('pages.configuration.followups.chatbot.med-alert-modal.no-med-alerts-message')}
          </p>
          <div className="c-flex c-justify-center c-gap-4">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
              {t('common.actions.cancel')}
            </CurecallButton>
            <CurecallButton onClick={onSwitchToNewMedAlert}>
              {t('pages.configuration.followups.chatbot.med-alert-modal.create-first-med-alert')}
            </CurecallButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      <div className="c-flex c-flex-col c-gap-6">
        <CurecallSearchableSelect
          label={t('pages.configuration.followups.chatbot.med-alert-modal.choose-existing-med-alert')}
          placeholder={t('pages.configuration.followups.chatbot.med-alert-modal.med-alert-placeholder')}
          items={MEDALERT_OPTIONS}
          onSelect={handleMedAlertSelect}
          defaultItemSelected={
            selectedMedAlert ? MEDALERT_OPTIONS.find((opt) => opt.value === selectedMedAlert.id) : undefined
          }
          optionsClassName="!c-max-h-44 [&>li]:c-py-2"
          autoComplete="off"
          errorMessage={showError ? t('common.form.required') : undefined}
        />
      </div>

      <div className="c-flex c-justify-center c-gap-8 c-pt-32">
        <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
          {t('common.actions.cancel')}
        </CurecallButton>
        <CurecallButton onClick={handleValidateExisting}>
          {t('pages.configuration.followups.chatbot.med-alert-modal.associate-med-alert')}
        </CurecallButton>
      </div>
    </div>
  )
}
